<template>
  <mce-group :class="$style['mce-bar-plot-vertical-grouped']">
    <mce-group
      v-for="(d, i) in data"
      :key="d.id"
      :class="$style['mce-bar-plot-vertical-grouped__group']"
      :data-mce-id="d.id"
      :offset="[xScalePadding[i], 0]"
      :width="xScale.bandwidth()"
    >
      <mce-bar-plot-vertical
        :color="color"
        :data="d.value"
        :domain-x="scaleBandDomain.groups[i]"
        :domain-y="domainY"
        :pattern-id="patternId"
        :range-x="[0, xScale.bandwidth()]"
        :range-y="rangeY"
        :data-focused="getFocusedIds(d)"
        @mouseenter-item="propagateEvent(EVENTS.MOUSEENTER_ITEM, $event)"
        @mouseleave-item="propagateEvent(EVENTS.MOUSELEAVE_ITEM, $event)"
      />
    </mce-group>
    <defs>
      <slot name="pattern" />
    </defs>
  </mce-group>
</template>

<script>
import MceBarPlotVertical from "@mce/bar-plot-vertical";
import { MceMouse, EVENTS } from "@mce/interactions";
import MceGroup from "@mce/group";
import { scaleBand } from "d3-scale";

export default {
  name: "AdlBarPlotVerticalGrouped",
  components: {
    MceBarPlotVertical,
    MceGroup,
  },
  mixins: [MceMouse()],
  props: {
    color: {
      type: Array,
      docs: {
        validation: "—",
        description:
          "Sets the colors of the bar plots. Colors will be used in the order provided and will repeat once reaching the end of the array if there are more bars than colors.",
      },
    },
    data: {
      type: Array,
      required: true,
      docs: {
        validation: "**Required**",
        description:
          "Sets the data to plot. See the [data format](#data-format) section below for more details.",
      },
    },
    dataFocused: {
      type: Array,
      docs: {
        validation: "—",
        description: "Sets specific data in focus for attention.",
      },
    },
    domainX: {
      type: Array,
      required: true,
      docs: {
        validation: "**Required**",
        description:
          "Sets the domain for `d3.scaleBand` used along the x axis.  See the [D3 scaleBand.domain documentation](https://github.com/d3/d3-scale/blob/main/README.md#band_domain) for more details.",
      },
    },
    domainY: {
      type: Array,
      required: true,
      docs: {
        validation: "**Required**",
        description:
          "Sets the domain for `d3.scaleLinear` used along the y axis.  See the [D3 scaleLinear documentation](https://github.com/d3/d3-scale/blob/main/README.md#linear-scales) for more details.",
      },
    },
    paddingInner: {
      type: Number,
      default: 0.1,
      docs: {
        validation: "—",
        description: "Sets the padding between the bars of the plot.",
      },
    },
    patternId: {
      type: Array,
      docs: {
        validation:
          "Referenced pattern IDs must exist in the `pattern` named slot.",
        description:
          "Applies the referenced patterns to the bar plot. Use the `pattern` named slot to define patterns. Patterns will be used in the order provided and will repeat once reaching the end of the array if there are more bars than patterns.",
      },
    },
    rangeX: {
      type: Array,
      required: true,
      docs: {
        validation: "**Required**",
        description:
          "Sets the range for `d3.scaleBand` used along the x axis.  See the [D3 scaleBand.range documentation](https://github.com/d3/d3-scale/blob/main/README.md#band_range) for more details.",
      },
    },
    rangeY: {
      type: Array,
      required: true,
      docs: {
        validation: "**Required**",
        description:
          "Sets the range for `d3.scaleLinear` used along the y axis.  See the [D3 scaleLinear documentation](https://github.com/d3/d3-scale/blob/main/README.md#linear-scales) for more details.",
      },
    },
  },
  data() {
    return {
      xScalePadding: [...Array(this.domainX.length).keys()],
      EVENTS,
    };
  },
  computed: {
    scaleBandDomain() {
      return {
        parent: [...Array(this.domainX.length).keys()],
        groups: this.data.map((d) => [...Array(d.value.length).keys()]),
      };
    },
    xScale() {
      let data = scaleBand()
        .domain(this.scaleBandDomain.parent)
        .range(this.rangeX)
        .paddingInner(this.paddingInner)
        .paddingOuter(this.paddingInner / 2);
      for (let i = this.domainX.length; i > 0; i--) {
        this.xScalePadding[i - 1] = data(i - 1) - data(0);
      }
      return data;
    },
  },
  methods: {
    getFocusedIds(group) {
      return this.dataFocused && this.dataFocused.includes(group.id)
        ? group.value.map((x) => x.id)
        : this.dataFocused;
    },
  },
};
</script>

<style lang="scss" module>
.mce-bar-plot-vertical-grouped,
.mce-bar-plot-vertical-grouped__group {
  shape-rendering: auto;
}
</style>
